import React, { useEffect, useMemo, useState } from "react";
import {
    Plus,
    Search,
    RefreshCw,
    CheckCircle2,
    XCircle,
    Pencil,
    ClipboardList,
    User2,
    CalendarDays,
} from "lucide-react";

const PRIORITY_LABEL = {
    1: "Bassa",
    2: "Media",
    3: "Alta",
    4: "Urgente",
};

function toISO(d) {
    return new Date(d).toISOString().slice(0, 10);
}

async function apiGet(url) {
    const r = await fetch(url, { credentials: "include" });
    const j = await r.json().catch(() => null);
    if (!j || j.ok !== true) throw new Error(j?.error || "Errore API");
    return j;
}

async function apiPost(url, body) {
    const r = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => null);
    if (!j || j.ok !== true) throw new Error(j?.error || "Errore API");
    return j;
}

export default function ScadenzeAdmin({ me, isSuper }) {
    const [statusTab, setStatusTab] = useState("open"); // open|done|cancelled
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [tasks, setTasks] = useState([]);

    // filtri
    const [q, setQ] = useState("");
    const [module, setModule] = useState("");
    const [priority, setPriority] = useState("");
    const [assignedTo, setAssignedTo] = useState("all"); // all|me|id
    const [dateField, setDateField] = useState("target"); // target|due

    const today = useMemo(() => new Date(), []);
    const [from, setFrom] = useState(toISO(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [to, setTo] = useState(toISO(new Date(today.getFullYear(), today.getMonth() + 1, 0)));

    // utenti per assegnazione (admin della stessa azienda)
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);

    // modal create/edit
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null); // task oppure null

    // log drawer
    const [logOpen, setLogOpen] = useState(false);
    const [logTask, setLogTask] = useState(null);
    const [logRows, setLogRows] = useState([]);
    const [logLoading, setLogLoading] = useState(false);

    const role = me?.ruolo || me?.role;
    const roles = Array.isArray(me?.roles) ? me.roles : [];
    const perms = Array.isArray(me?.permessi) ? me.permessi : [];

    const canManage =
        role === "admin" ||
        role === "superadmin" ||
        roles.includes("admin") ||
        roles.includes("superadmin") ||
        perms.includes("ALL") ||
        isSuper;

    async function loadTasks() {
        setLoading(true);
        setErr("");
        try {
            const params = new URLSearchParams();
            params.set("from", from);
            params.set("to", to);
            params.set("status", statusTab);
            params.set("date_field", dateField);

            if (assignedTo) params.set("assigned_to", assignedTo);
            if (priority !== "") params.set("priority", priority);
            if (module) params.set("module", module);
            if (q) params.set("q", q);

            const data = await apiGet(`/api/tasks/list.php?${params.toString()}`);
            setTasks(Array.isArray(data.tasks) ? data.tasks : []);
        } catch (e) {
            setErr(e.message || "Errore");
        } finally {
            setLoading(false);
        }
    }

    async function loadUsers() {
        // per dropdown assegnazione
        setUsersLoading(true);
        try {
            const data = await apiGet("/api/tasks/users.php");
            setUsers(Array.isArray(data.users) ? data.users : []);
        } catch {
            // non bloccare la UI se endpoint non c'è ancora
            setUsers([]);
        } finally {
            setUsersLoading(false);
        }
    }

    useEffect(() => {
        loadTasks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusTab]);

    useEffect(() => {
        if (canManage) loadUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canManage]);

    async function onComplete(id) {
        try {
            await apiPost("/api/tasks/complete.php", { id });
            await loadTasks();
        } catch (e) {
            alert(e.message || "Errore");
        }
    }

    async function onCancel(id) {
        const reason = prompt("Motivo annullamento (opzionale):") || "";
        try {
            await apiPost("/api/tasks/cancel.php", { id, reason });
            await loadTasks();
        } catch (e) {
            alert(e.message || "Errore");
        }
    }

    async function onQuickAssign(task, userId) {
        try {
            await apiPost("/api/tasks/update.php", { id: task.id, assigned_to: userId || null });
            await loadTasks();
        } catch (e) {
            alert(e.message || "Errore");
        }
    }

    async function openLog(task) {
        setLogTask(task);
        setLogOpen(true);
        setLogLoading(true);
        setLogRows([]);
        try {
            const data = await apiGet(`/api/tasks/log.php?id=${task.id}`);
            setLogRows(Array.isArray(data.log) ? data.log : []);
        } catch (e) {
            setLogRows([]);
        } finally {
            setLogLoading(false);
        }
    }

    function openCreate() {
        setEditing(null);
        setModalOpen(true);
    }

    function openEdit(task) {
        setEditing(task);
        setModalOpen(true);
    }

    async function saveTask(payload) {
        try {
            if (editing) {
                await apiPost("/api/tasks/update.php", { id: editing.id, ...payload });
            } else {
                await apiPost("/api/tasks/create.php", payload);
            }
            setModalOpen(false);
            setEditing(null);
            await loadTasks();
        } catch (e) {
            alert(e.message || "Errore salvataggio");
        }
    }

    return (
        <div className="bg-white shadow-md rounded-2xl p-4 md:p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
                        <ClipboardList size={20} /> Attività & Scadenze
                    </div>
                    <div className="text-sm text-slate-500">
                        Gestione operativa task (filtri, assegnazioni, priorità, chiusure, log).
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={loadTasks}
                        className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 flex items-center gap-2"
                    >
                        <RefreshCw size={16} /> Aggiorna
                    </button>
                    {canManage && (
                        <button
                            onClick={openCreate}
                            className="px-3 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white flex items-center gap-2"
                        >
                            <Plus size={16} /> Nuova attività
                        </button>
                    )}
                </div>
            </div>

            {/* Tabs status */}
            <div className="mt-4 flex gap-2 flex-wrap">
                {["open", "done", "cancelled"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setStatusTab(s)}
                        className={
                            statusTab === s
                                ? "px-4 py-2 rounded-full bg-slate-900 text-white shadow-sm"
                                : "px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                        }
                    >
                        {s === "open" ? "Da fare" : s === "done" ? "Completate" : "Annullate"}
                    </button>
                ))}
            </div>

            {/* Filtri */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-3">
                    <label className="text-xs text-slate-600">Dal</label>
                    <input
                        type="date"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                    />
                </div>
                <div className="md:col-span-3">
                    <label className="text-xs text-slate-600">Al</label>
                    <input
                        type="date"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="text-xs text-slate-600">Campo data</label>
                    <select
                        value={dateField}
                        onChange={(e) => setDateField(e.target.value)}
                        className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                    >
                        <option value="target">Giorno calendario</option>
                        <option value="due">Scadenza (due)</option>
                    </select>
                </div>
                <div className="md:col-span-2">
                    <label className="text-xs text-slate-600">Priorità</label>
                    <select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                        className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                    >
                        <option value="">Tutte</option>
                        <option value="4">Urgente</option>
                        <option value="3">Alta</option>
                        <option value="2">Media</option>
                        <option value="1">Bassa</option>
                    </select>
                </div>
                <div className="md:col-span-2">
                    <label className="text-xs text-slate-600">Assegnate a</label>
                    <select
                        value={assignedTo}
                        onChange={(e) => setAssignedTo(e.target.value)}
                        className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                    >
                        <option value="all">Tutte</option>
                        <option value="me">Solo mie</option>
                        {canManage && users.map((u) => (
                            <option key={u.id} value={String(u.id)}>
                                {u.nome || u.email} ({u.ruolo})
                            </option>
                        ))}
                    </select>
                </div>

                <div className="md:col-span-3">
                    <label className="text-xs text-slate-600">Modulo</label>
                    <input
                        value={module}
                        onChange={(e) => setModule(e.target.value)}
                        placeholder='es. "cryo" / "sanificazione"'
                        className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                    />
                </div>

                <div className="md:col-span-9">
                    <label className="text-xs text-slate-600">Cerca</label>
                    <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
                        <Search size={16} className="text-slate-400" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Titolo o descrizione…"
                            className="w-full outline-none"
                        />
                    </div>
                </div>

                <div className="md:col-span-12 flex justify-end">
                    <button
                        onClick={loadTasks}
                        className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2"
                    >
                        <CalendarDays size={16} /> Applica filtri
                    </button>
                </div>
            </div>

            {/* Error / Loading */}
            {err && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {err}
                </div>
            )}

            {/* Lista */}
            <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-left text-slate-500">
                        <tr className="border-b">
                            <th className="py-2">Titolo</th>
                            <th className="py-2">Date</th>
                            <th className="py-2">Priorità</th>
                            <th className="py-2">Assegnata</th>
                            <th className="py-2">Modulo</th>
                            <th className="py-2 text-right">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="py-6 text-slate-500">Caricamento…</td></tr>
                        ) : tasks.length === 0 ? (
                            <tr><td colSpan={6} className="py-6 text-slate-500">Nessuna attività nel periodo.</td></tr>
                        ) : (
                            tasks.map((t) => (
                                <tr key={t.id} className="border-b hover:bg-slate-50">
                                    <td className="py-3">
                                        <div className="font-medium text-slate-900">{t.title}</div>
                                        {t.description && <div className="text-slate-500 line-clamp-2">{t.description}</div>}
                                        <button
                                            onClick={() => openLog(t)}
                                            className="mt-1 text-xs text-indigo-600 hover:underline"
                                        >
                                            Vedi storico
                                        </button>
                                    </td>
                                    <td className="py-3 text-slate-700">
                                        <div>Cal: {t.target_date || "—"}</div>
                                        <div>Due: {t.due_date || "—"}</div>
                                    </td>
                                    <td className="py-3">
                                        <span
                                            className={
                                                t.priority >= 4
                                                    ? "px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs"
                                                    : t.priority === 3
                                                        ? "px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-xs"
                                                        : "px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs"
                                            }
                                        >
                                            {PRIORITY_LABEL[t.priority] || t.priority}
                                        </span>
                                    </td>
                                    <td className="py-3 text-slate-700">
                                        {canManage ? (
                                            <div className="flex items-center gap-2">
                                                <User2 size={16} className="text-slate-400" />
                                                <select
                                                    value={t.assigned_to ? String(t.assigned_to) : ""}
                                                    onChange={(e) => onQuickAssign(t, e.target.value ? parseInt(e.target.value, 10) : null)}
                                                    className="rounded-lg border border-slate-200 px-2 py-1"
                                                >
                                                    <option value="">(non assegnata)</option>
                                                    {users.map((u) => (
                                                        <option key={u.id} value={String(u.id)}>
                                                            {u.nome || u.email} ({u.ruolo})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        ) : (
                                            <div>
                                                {t.assigned_to
                                                    ? (t.assigned_to_name || t.assigned_to_email || `Utente #${t.assigned_to}`)
                                                    : "—"}
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-3 text-slate-700">
                                        <div className="text-xs">{t.source_module || "custom"}</div>
                                        {t.source_ref ? <div className="text-xs text-slate-500">ref: {t.source_ref}</div> : null}
                                    </td>
                                    <td className="py-3 text-right">
                                        <div className="flex justify-end gap-2">
                                            {canManage && statusTab === "open" && (
                                                <button
                                                    onClick={() => openEdit(t)}
                                                    className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 inline-flex items-center gap-1"
                                                >
                                                    <Pencil size={14} /> Modifica
                                                </button>
                                            )}
                                            {statusTab === "open" && (
                                                <button
                                                    onClick={() => onComplete(t.id)}
                                                    className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1"
                                                >
                                                    <CheckCircle2 size={14} /> Completa
                                                </button>
                                            )}
                                            {canManage && statusTab === "open" && (
                                                <button
                                                    onClick={() => onCancel(t.id)}
                                                    className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-1"
                                                >
                                                    <XCircle size={14} /> Annulla
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal Create/Edit */}
            {modalOpen && (
                <TaskModal
                    canManage={canManage}
                    users={users}
                    usersLoading={usersLoading}
                    editing={editing}
                    onClose={() => { setModalOpen(false); setEditing(null); }}
                    onSave={saveTask}
                />
            )}

            {/* Drawer log */}
            {logOpen && (
                <LogDrawer
                    task={logTask}
                    loading={logLoading}
                    rows={logRows}
                    onClose={() => setLogOpen(false)}
                />
            )}
        </div>
    );
}

function TaskModal({ editing, onClose, onSave, users, usersLoading, canManage }) {
    const [title, setTitle] = useState(editing?.title || "");
    const [description, setDescription] = useState(editing?.description || "");
    const [target_date, setTargetDate] = useState(
        editing?.target_date || new Date().toISOString().slice(0, 10)
    );
    const [due_date, setDueDate] = useState(editing?.due_date || "");
    const [priority, setPriority] = useState(String(editing?.priority || 2));
    const [assigned_to, setAssignedTo] = useState(editing?.assigned_to ? String(editing.assigned_to) : "");
    const [visibility, setVisibility] = useState(editing?.visibility || "company");
    const [visible_role, setVisibleRole] = useState(editing?.visible_role || "utente");
    const [source_module, setSourceModule] = useState(editing?.source_module || "custom");
    const [source_ref, setSourceRef] = useState(editing?.source_ref || "");

    const isEdit = !!editing;

    function submit() {
        const payload = {
            title,
            description,
            target_date: target_date || null,
            due_date: due_date || null,
            priority: parseInt(priority, 10),
            source_module: source_module || "custom",
            source_ref: source_ref ? parseInt(source_ref, 10) : null,
        };

        // solo admin/superadmin gestiscono assegnazione/visibility
        if (canManage) {
            payload.assigned_to = assigned_to ? parseInt(assigned_to, 10) : null;
            payload.visibility = visibility;
            if (visibility === "role") payload.visible_role = visible_role || "utente";
        }

        onSave(payload);
    }

    return (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl p-4 md:p-6">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-xl font-semibold text-slate-900">
                            {isEdit ? "Modifica attività" : "Nuova attività"}
                        </div>
                        <div className="text-sm text-slate-500">
                            Compila i campi principali e salva.
                        </div>
                    </div>
                    <button onClick={onClose} className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200">
                        Chiudi
                    </button>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-12">
                        <label className="text-xs text-slate-600">Titolo</label>
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                        />
                    </div>

                    <div className="md:col-span-12">
                        <label className="text-xs text-slate-600">Descrizione</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                        />
                    </div>

                    <div className="md:col-span-3">
                        <label className="text-xs text-slate-600">Calendario</label>
                        <input
                            type="date"
                            value={target_date || ""}
                            onChange={(e) => setTargetDate(e.target.value)}
                            className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                        />
                    </div>
                    <div className="md:col-span-3">
                        <label className="text-xs text-slate-600">Scadenza</label>
                        <input
                            type="date"
                            value={due_date || ""}
                            onChange={(e) => setDueDate(e.target.value)}
                            className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                        />
                    </div>
                    <div className="md:col-span-3">
                        <label className="text-xs text-slate-600">Priorità</label>
                        <select
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                            className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                        >
                            <option value="4">Urgente</option>
                            <option value="3">Alta</option>
                            <option value="2">Media</option>
                            <option value="1">Bassa</option>
                        </select>
                    </div>

                    <div className="md:col-span-3">
                        <label className="text-xs text-slate-600">Modulo</label>
                        <input
                            value={source_module}
                            onChange={(e) => setSourceModule(e.target.value)}
                            className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                        />
                    </div>

                    <div className="md:col-span-3">
                        <label className="text-xs text-slate-600">source_ref</label>
                        <input
                            value={source_ref}
                            onChange={(e) => setSourceRef(e.target.value)}
                            placeholder="id collegato (opzionale)"
                            className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                        />
                    </div>

                    {canManage && (
                        <>
                            <div className="md:col-span-4">
                                <label className="text-xs text-slate-600">Assegna a</label>
                                <select
                                    value={assigned_to}
                                    onChange={(e) => setAssignedTo(e.target.value)}
                                    className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                                    disabled={usersLoading}
                                >
                                    <option value="">(non assegnata)</option>
                                    {users.map((u) => (
                                        <option key={u.id} value={String(u.id)}>
                                            {u.nome || u.email} ({u.ruolo})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="md:col-span-4">
                                <label className="text-xs text-slate-600">Visibilità</label>
                                <select
                                    value={visibility}
                                    onChange={(e) => setVisibility(e.target.value)}
                                    className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                                >
                                    <option value="company">Azienda</option>
                                    <option value="private">Privata</option>
                                    <option value="role">Per ruolo</option>
                                </select>
                            </div>

                            {visibility === "role" && (
                                <div className="md:col-span-4">
                                    <label className="text-xs text-slate-600">Ruolo</label>
                                    <select
                                        value={visible_role}
                                        onChange={(e) => setVisibleRole(e.target.value)}
                                        className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                                    >
                                        <option value="admin">admin</option>
                                        <option value="utente">utente</option>
                                        <option value="superadmin">superadmin</option>
                                    </select>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="mt-4 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200"
                    >
                        Annulla
                    </button>
                    <button
                        onClick={submit}
                        className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white"
                    >
                        Salva
                    </button>
                </div>
            </div>
        </div>
    );
}

function LogDrawer({ task, rows, loading, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-black/30" onClick={onClose} />
            <div className="w-full max-w-xl bg-white shadow-2xl p-4 md:p-6 overflow-y-auto">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-lg font-semibold text-slate-900">Storico attività</div>
                        <div className="text-sm text-slate-500">{task?.title}</div>
                    </div>
                    <button onClick={onClose} className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200">
                        Chiudi
                    </button>
                </div>

                <div className="mt-4">
                    {loading ? (
                        <div className="text-slate-500">Caricamento storico…</div>
                    ) : rows.length === 0 ? (
                        <div className="text-slate-500">Nessun evento log.</div>
                    ) : (
                        <div className="space-y-3">
                            {rows.map((r) => (
                                <div key={r.id} className="rounded-xl border border-slate-200 p-3">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm font-semibold text-slate-900">{r.action}</div>
                                        <div className="text-xs text-slate-500">{r.created_at}</div>
                                    </div>
                                    {r.message && <div className="text-sm text-slate-700 mt-1">{r.message}</div>}
                                    <details className="mt-2">
                                        <summary className="text-xs text-indigo-600 cursor-pointer">Dettagli</summary>
                                        <pre className="text-xs bg-slate-50 p-2 rounded-lg overflow-auto mt-2">
                                            {JSON.stringify({ before: r.before_json, after: r.after_json }, null, 2)}
                                        </pre>
                                    </details>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
